// Dig state (SPEC F6): heard, bad and favourite records, the play history,
// and cached crate shards. All writes go through here (CLAUDE.md).

import type { CrateCache, HistoryEntry, RecordState, StyleId } from '../../data/types';
import { getDb } from '../schema';

export async function loadRecordStates(): Promise<{ heardAt: Record<string, number>; bad: Set<string>; notRecord: Map<string, NonNullable<RecordState['notRecord']>>; favourites: Set<string> }> {
  const all = await getDb().records.toArray();
  const heardAt: Record<string, number> = {};
  const bad = new Set<string>();
  const notRecord = new Map<string, NonNullable<RecordState['notRecord']>>();
  const favourites = new Set<string>();
  for (const r of all) {
    if (r.heardAt !== undefined) heardAt[r.videoId] = r.heardAt;
    if (r.bad) bad.add(r.videoId);
    if (r.notRecord) notRecord.set(r.videoId, r.notRecord);
    if (r.favourite) favourites.add(r.videoId);
  }
  return { heardAt, bad, notRecord, favourites };
}

/** A record was dug or played: history and its last-heard time change together. */
export async function recordHeard(videoId: string, styleId: StyleId, at: number, outcome: HistoryEntry['outcome'] = 'played'): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.history, db.records, async () => {
    await db.history.add({ videoId, at, styleId, outcome });
    const cur = (await db.records.get(videoId)) ?? { videoId };
    await db.records.put({ ...cur, heardAt: at });
  });
}

/** Error 100, 101 or 150: never dug again. */
export async function recordBad(videoId: string, styleId: StyleId, at: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.history, db.records, async () => {
    await db.history.add({ videoId, at, styleId, outcome: 'bad' });
    const cur = (await db.records.get(videoId)) ?? { videoId };
    await db.records.put({ ...cur, bad: true, heardAt: at });
  });
}

/** Dig › Not a record (SPEC F6): never dug again, with the reason; undefined clears it (Undo). A plain field on the record, like `bad`. */
export async function setNotRecord(videoId: string, reason: RecordState['notRecord'], at: number): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.records, async () => {
    const cur: RecordState = (await db.records.get(videoId)) ?? { videoId };
    const next: RecordState = { ...cur, heardAt: cur.heardAt ?? at };
    if (reason) next.notRecord = reason;
    else delete next.notRecord;
    await db.records.put(next);
  });
}

/** Records marked Not a record, for the tuning export in Dig › Channels. */
export async function listNotRecords(): Promise<{ videoId: string; reason: NonNullable<RecordState['notRecord']> }[]> {
  const all = await getDb().records.filter((r) => r.notRecord !== undefined).toArray();
  return all.map((r) => ({ videoId: r.videoId, reason: r.notRecord as NonNullable<RecordState['notRecord']> }));
}

export async function setFavourite(videoId: string, favourite: boolean): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.records, async () => {
    const cur: RecordState = (await db.records.get(videoId)) ?? { videoId };
    await db.records.put({ ...cur, favourite });
  });
}

/** Newest first. */
export const listHistory = (limit = 100): Promise<HistoryEntry[]> => getDb().history.orderBy('at').reverse().limit(limit).toArray();

export async function saveCrate(styleId: StyleId, shard: unknown, cachedAt: number): Promise<void> {
  await getDb().crates.put({ styleId, shard, cachedAt });
}

export const deleteCrate = (styleId: StyleId): Promise<void> => getDb().crates.delete(styleId);

export const loadCrate = (styleId: StyleId): Promise<CrateCache | undefined> => getDb().crates.get(styleId);

/** Every cached shard, for looking up a reopened record outside the current style (callers drop expired ones). */
export const listCrates = (): Promise<CrateCache[]> => getDb().crates.toArray();

/** Records with the heart on, most recently heard first (Dig › History, Favourites only). */
export async function listFavourites(): Promise<RecordState[]> {
  const all = await getDb().records.filter((r) => r.favourite === true).toArray();
  return all.sort((a, b) => (b.heardAt ?? 0) - (a.heardAt ?? 0));
}

/**
 * YouTube Developer Policies III.E.4.d: API data, titles included, is not kept past 30 days, and marks and history
 * are kept longer, so they hold a video ID and our own fields only. Older versions stored the title: this removes it
 * from marks and records. Safe to run on every start (it only touches rows that still have one). Returns how many it cleaned.
 */
export async function stripStoredTitles(): Promise<number> {
  const db = getDb();
  const has = (r: object) => 'title' in r;
  // `title` is no longer on the types, but rows written by older versions still carry it
  const strip = (r: object) => {
    delete (r as { title?: unknown }).title;
  };
  return db.transaction('rw', db.marks, db.records, async () => {
    const marks = await db.marks.filter(has).modify(strip);
    const records = await db.records.filter(has).modify(strip);
    return marks + records;
  });
}
